import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import InactivityManager from 'utils/InactivityManager';
import CrayonStrokeGame from 'components/CrayonStroke/CrayonStrokeGame';
import DrawActivityTutorial from 'components/CrayonStroke/CrayonStrokeIntro';
import AssetLoader from 'utils/assetLoader';
import './CrayonStroke.scss';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import ZoomScrollManager from 'utils/managers/activity-zoom-scroll-manager';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotStartDrawing: 'Did not start drawing',
  didNotTouchIcon: 'Did not touch smiley nor arrow'
};

export class CrayonStroke extends Component {
  constructor(props) {
    super(props);

    this.zoomScrollManager = new ZoomScrollManager(this.activityContainerClassname, newTolerance =>
      this.setState({ zoomToleranceFactor: newTolerance })
    );

    this.state = {
      renderIntro: false,
      renderGame: false,
      soundLoaded: false,
      playedFirstDigit: false,
      zoomToleranceFactor: 1
    };
    this.svgSoundsList = null;
    this.characterSvgPath = null;
    this.inactivityManager = null;

    this.activityContainerClassname = 'content';
  }

  playInstructions(watched) {
    const {
      audio,
      data: {
        instructions: { beforeDraw }
      }
    } = this.props.currentRound;

    if (watched) {
      audio[beforeDraw[0].src].play();
      return audio[beforeDraw[0].src];
    }

    audio[beforeDraw[0].src].once('end', () => {
      this.setState({ renderIntro: true, playedFirstDigit: true });
    });

    audio[beforeDraw[0].src].play();

    return audio[beforeDraw[0].src];
  }

  componentDidMount() {
    const {
      props: {
        currentRound: {
          data: {
            instructions: { beforeDraw }
          }
        }
      }
    } = this;

    if (beforeDraw) {
      if (this.props.level < 2) {
        this.inactivityManager = new InactivityManager(
          TIMEOUT,
          () => this.playInstructions(true),
          REASON.didNotTouchIcon
        );
        this.inactivityManager.watch();
      }
      this.playInstructions();
    } else {
      this.setState({ renderIntro: true });
    }

    this.zoomScrollManager.initialize();
  }

  async getSvgSoundsList() {
    const { segments } = this.props.currentRound.data.svg;
    const assetLoader = new AssetLoader();

    segments.forEach(segment => assetLoader.queueAudio(segment.audio));

    const cache = await assetLoader.downloadAll();
    return segments.reduce((map, key) => {
      if (key.audio?.src) map[key.id] = cache.get(key.audio?.src);

      return map;
    }, {});
  }

  handleSvgLoaded = async () => {
    this.svgSoundsList = await this.getSvgSoundsList();
    this.setState({ soundLoaded: true });
  };

  handleIntroComplete = () => {
    if (this.props.level !== 1) {
      this.inactivityManager = new InactivityManager(
        TIMEOUT,
        () => this.playInstructions(true),
        REASON.didNotStartDrawing
      );

      const {
        props: {
          character,
          currentRound: {
            audio,
            data: {
              instructions: { beforeDraw }
            }
          }
        }
      } = this;

      this.inactivityManager.clear();
      if (character.length > 1) {
        if (beforeDraw[2]) {
          const cb = () => {
            audio[beforeDraw[2].src].play();
            return audio[beforeDraw[2].src];
          };

          this.inactivityManager.watch(TIMEOUT, cb, REASON.didNotStartDrawing);
          audio[beforeDraw[2].src].play();
        }
      } else {
        if (beforeDraw[1]) {
          const cb = () => {
            audio[beforeDraw[1].src].play();
            return audio[beforeDraw[1].src];
          };

          this.inactivityManager.watch(TIMEOUT, cb, REASON.didNotStartDrawing);
          audio[beforeDraw[1].src].play();
        }
      }
      this.setState({ renderGame: true });
    } else {
      this.props.onRoundEnd();
    }
  };

  handleNextInstruction = () => {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { beforeDraw }
          }
        }
      }
    } = this;
    const sound = audio[beforeDraw[1].src];

    if (this.props.level < 2) {
      this.inactivityManager.clear();
      this.inactivityManager.watch(
        TIMEOUT,
        () => {
          sound.play();
          return sound;
        },
        REASON.didNotTouchIcon
      );
    }
    sound.play();
  };

  componentWillUnmount() {
    if (this.inactivityManager) {
      this.inactivityManager.clear();
    }

    this.props.currentRound.stopAllSounds();
    if (this.svgSoundsList) {
      Object.keys(this.svgSoundsList).forEach(key => {
        if (this.svgSoundsList[key] !== null) {
          this.svgSoundsList[key].unload(true);
        }
      });
    }

    this.zoomScrollManager.removeWindowResizeEvents();
  }

  render() {
    const {
      currentRound: {
        data: { svg }
      }
    } = this.props;
    const locale = getLocale();

    const { zoomToleranceFactor } = this.state;
    const {
      handleGameAutoScroll,
      handleIntroAutoScroll,
      resetAutoScrollPosition
    } = this.zoomScrollManager;

    return (
      <div
        className={this.activityContainerClassname}
        tabIndex={this.zoomScrollManager.isZoomed() ? '0' : null}
      >
        <div className="draw-activity-container">
          {this.state.renderIntro && this.state.soundLoaded ? (
            <DrawActivityTutorial
              {...this.props}
              svgSounds={this.svgSoundsList}
              onComplete={this.handleIntroComplete}
              onNextInstruction={this.handleNextInstruction}
              inactivityManager={this.inactivityManager}
              locale={locale}
              segments={svg.segments}
              svgSrc={svg.src}
              svgDescription={svg.description}
              handleAutoScroll={handleIntroAutoScroll}
            />
          ) : null}
          {this.state.renderGame ? (
            <CrayonStrokeGame
              {...this.props}
              done={this.props.onRoundEnd}
              inactivityManager={this.inactivityManager}
              segments={svg.segments}
              svgSrc={svg.src}
              zoomToleranceFactor={zoomToleranceFactor}
              handleAutoScroll={handleGameAutoScroll}
              resetAutoScroll={resetAutoScrollPosition}
            />
          ) : null}
          <object
            tabIndex="-1"
            style={{ width: 0, height: 0, position: 'absolute', top: 0, left: 0 }}
            data={svg.src}
            type="image/svg+xml"
            id="alphasvg"
            onLoad={this.handleSvgLoaded}
          />
        </div>
      </div>
    );
  }
}

CrayonStroke.propTypes = {
  character: PropTypes.string,
  level: PropTypes.number,
  currentRound: PropTypes.shape({
    activityType: PropTypes.string,
    images: PropTypes.array,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.shape({
        beforeDraw: PropTypes.array
      }),
      crayonDrawing: PropTypes.object,
      clink: PropTypes.object,
      svg: PropTypes.shape({
        segments: PropTypes.array,
        src: PropTypes.string,
        description: PropTypes.string
      })
    }),
    stopAllSounds: PropTypes.func
  }),
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func
};

const mapStateToProps = state => ({
  character: state.activity.data.character.character
});

export default connect(mapStateToProps)(CrayonStroke);
